import { useFormContext } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFetchEventCategories } from "@/features/event-categories/api/fetch-event-categories";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { EventType } from "@classview/shared-types";

import { EventSchema } from "../types/manage-event.schema";

const eventTypesToFormOptions: Record<
  EventType,
  { label: string; description: string }
> = {
  [EventType.WORKSHOP]: {
    label: "Workshop",
    description:
      "All users will be taking part, works best for small groups (up to 10 people)",
  },
  [EventType.PRESENTATION]: {
    label: "Presentation",
    description:
      "One or several users will be talking & sharing their screen, attendees can take part but are muted by default",
  },
  [EventType.LECTURE]: {
    label: "Lecture",
    description:
      "Only the host or designated user will be talking, attendees cannot unmute themselves.",
  },
};

export function ManageEventMetadataElement() {
  const form = useFormContext<EventSchema>();
  const { organisation } = useOrganisation();
  const { data: eventCategories = [], isLoading } = useFetchEventCategories({
    organisationId: organisation?.id ?? "",
  });

  const currentCategory = form.watch("category");
  const categoryOptions = [...eventCategories];

  // If current category is not in the list, add it as an option (for editing existing events)
  if (
    currentCategory &&
    !categoryOptions.some((cat) => cat.name === currentCategory)
  ) {
    categoryOptions.push({ id: "", name: currentCategory });
  }

  return (
    <>
      <FormLabel>Metadata</FormLabel>
      <div className="mt-4 flex flex-col gap-4">
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel isRequired>Category</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={isLoading}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        categoryOptions.length === 0
                          ? "No category found"
                          : "Select a category..."
                      }
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {categoryOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No category found
                    </div>
                  ) : (
                    categoryOptions.map((category) => (
                      <SelectItem
                        key={category.id || category.name}
                        value={category.name}
                      >
                        {category.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Event Type</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="flex flex-col space-y-1"
                >
                  {Object.entries(eventTypesToFormOptions).map(
                    ([type, { label, description }]) => (
                      <FormItem className="flex space-x-3 space-y-0" key={type}>
                        <FormControl>
                          <RadioGroupItem value={type} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="font-normal">{label}</FormLabel>
                          <FormDescription>{description}</FormDescription>
                        </div>
                      </FormItem>
                    ),
                  )}
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </>
  );
}
